import PortalIdParser from 'PortalIdParser';

import { CrmSearchAllProperties } from 'hs-resources/resources/CrmSearch';
import {
  CrmSearchAllPropertiesArgs,
  CrmObject as PulseCrmObject,
  CrmSearchResults,
} from 'hs-resources/resources/CrmSearch/types';

import { CrmObject, CrmObjectTypeProperty } from 'marketing-platform-lib/types';
import {
  CrmSearchRequestOptions,
  CrmSearchResponse,
  CrmSearchVariables,
} from 'framework-listing-lib/types';

export { CrmSearchAllProperties };

type PulseCrmSearchResults = CrmSearchResults<PulseCrmObject>;

/**
 * The CrmSearchAllProperties resource always fetches every property and hardcodes
 * its own requestOptions, so `properties`, `requestOptions` and `associationSpecs`
 * from our request are intentionally dropped when mapping onto its narrower args.
 */
export function toPulseArgs(
  fetchParams: CrmSearchRequestOptions
): CrmSearchAllPropertiesArgs {
  return {
    // filterGroups/sorts are wire-identical to the crm-search request the
    // GraphQL path already sends these same values as; the two packages just
    // declare structurally different (but runtime-compatible) filter types.
    filterGroups:
      fetchParams.filterGroups as CrmSearchAllPropertiesArgs['filterGroups'],
    objectTypeId: fetchParams.objectTypeId,
    sorts: (fetchParams.sorts ?? null) as CrmSearchAllPropertiesArgs['sorts'],
    query: fetchParams.query ?? null,
    count: fetchParams.count ?? null,
    offset: fetchParams.offset ?? null,
    timeZoneSource: 'USER',
  };
}

export function getPulseArgs(
  variables: CrmSearchVariables
): CrmSearchAllPropertiesArgs {
  return toPulseArgs(variables.fetchParams);
}

function transformPulseCrmObject(pulseObject: PulseCrmObject): CrmObject {
  const properties: Record<string, CrmObjectTypeProperty> = {};
  for (const key of Object.keys(pulseObject.properties)) {
    const property = pulseObject.properties[key];
    if (property) {
      properties[key] = {
        source: property.source,
        sourceId: property.sourceId,
        timestamp: property.timestamp,
        value: property.value ?? null,
      };
    }
  }

  return {
    objectId: pulseObject.objectId,
    objectTypeId: pulseObject.objectTypeId,
    portalId: Number(PortalIdParser.get()),
    properties,
    currentUserPermissions: pulseObject.currentUserPermissions,
  };
}

/**
 * Adapts the hs-resources CrmSearchAllProperties payload into the CrmSearchResponse
 * shape the rest of listing-lib expects. Associated objects are not fetched by this
 * resource, so `associatedObjects` is omitted; useHasPulseCrmSearch keeps consumers
 * that request `associationSpecs` off the Pulse path.
 */
export function transformPulseResponseToCrmSearchResponse(
  pulseData: PulseCrmSearchResults | undefined
): CrmSearchResponse | undefined {
  return pulseData ? toCrmSearchResponse(pulseData) : undefined;
}

function toCrmSearchResponse(
  pulseData: PulseCrmSearchResults
): CrmSearchResponse {
  return {
    total: pulseData.total,
    offset: pulseData.offset,
    hasMore: pulseData.hasMore,
    results: pulseData.results.map(transformPulseCrmObject),
  };
}

function transformCrmObjectToPulseCrmObject(
  crmObject: CrmObject
): PulseCrmObject {
  const properties: Record<string, PulseCrmObject['properties'][string]> = {};
  for (const key of Object.keys(crmObject.properties)) {
    const property = crmObject.properties[key];
    properties[key] = {
      name: key,
      value: property.value == null ? undefined : String(property.value),
      source: property.source ?? undefined,
      sourceId: property.sourceId ?? undefined,
      timestamp: property.timestamp,
    };
  }

  return {
    objectId: Number(crmObject.objectId),
    objectTypeId: crmObject.objectTypeId,
    currentUserPermissions:
      crmObject.currentUserPermissions as PulseCrmObject['currentUserPermissions'],
    properties,
    __allProperties: true,
  };
}

/**
 * Inverse of transformPulseResponseToCrmSearchResponse. Fields with no analog in
 * CrmSearchResponse (portalId, property `name`, canMerge) are reconstructed; none
 * of them are surfaced downstream, so the round-trip is lossless for consumers.
 */
export function transformCrmSearchResponseToPulseResponse(
  response: CrmSearchResponse
): PulseCrmSearchResults {
  return {
    total: response.total,
    offset: response.offset,
    hasMore: response.hasMore,
    results: response.results.map(transformCrmObjectToPulseCrmObject),
  };
}

/**
 * Applies a CrmSearchResponse-shaped optimistic updater to the stored Pulse cache
 * data by transforming out to the CrmSearchResponse shape, running the updater, and
 * transforming back into the resource's own payload shape.
 */
export function updatePulseCacheData(
  currentData: PulseCrmSearchResults,
  updater: (response: CrmSearchResponse) => CrmSearchResponse
): PulseCrmSearchResults {
  return transformCrmSearchResponseToPulseResponse(
    updater(toCrmSearchResponse(currentData))
  );
}
